import { AppError } from '@shared/api/error'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import type { BankAccountReadiness } from '../api/verification'

type BankPayoutBlockReason = NonNullable<BankAccountReadiness['reason']>

const readinessMessages: Record<BankPayoutBlockReason, string> = {
  not_observed: 'Aguardando a primeira consulta da conta bancária.',
  no_default_account: 'Cadastre a conta bancária que vai receber os repasses.',
  default_account_errored:
    'Um repasse para esta conta falhou. Cadastre outra conta ou confira os dados com o banco.',
  default_account_verification_failed: 'A conta não pôde ser validada. Cadastre outra conta.',
  external_account_required: 'Cadastre uma conta bancária para liberar os repasses.',
  payouts_disabled:
    'Os repasses ainda não foram liberados. Conclua as demais etapas da verificação.',
}

export function describeBankReadiness(readiness: BankAccountReadiness): string {
  if (readiness.ready) return 'Conta pronta para receber repasses'
  return readiness.reason ? readinessMessages[readiness.reason] : readinessMessages.not_observed
}

/**
 * `message`, `fields` and `profile` ask the seller to change something and stay next to the form;
 * `retry` and `switch_failed` only need another attempt and go to a toast.
 */
export type BankAccountErrorFeedback =
  | { kind: 'message'; message: string }
  | { kind: 'retry'; message: string }
  | { kind: 'switch_failed'; message: string }
  | {
      kind: 'fields'
      fields: Partial<Record<'bankCode' | 'branch' | 'accountNumber', string>>
    }
  | {
      kind: 'profile'
      message: string
      actionLabel: string
      actionTo: '/onboarding'
    }
  | { kind: 'refetch' }

export function describeBankAccountError(
  status: number,
  code: string,
  hasActiveAccount: boolean,
  param?: string,
  registrationFeedbackEnabled = false,
): BankAccountErrorFeedback {
  if (status === 409 && code === 'not_linked') return { kind: 'refetch' }

  if (registrationFeedbackEnabled && [401, 403, 429].includes(status)) {
    return {
      kind: 'message',
      message: getUserFacingError(new AppError({ status, code, message: '' })).description,
    }
  }

  if (registrationFeedbackEnabled && status === 409 && code === 'step_unavailable') {
    return {
      kind: 'message',
      message: 'Esta etapa não está disponível no momento. Confira as pendências do cadastro.',
    }
  }

  if (status === 409 && code === 'holder_name_missing') {
    return {
      kind: 'profile',
      message: 'Complete o nome do titular ou a razão social no perfil antes de cadastrar a conta.',
      actionLabel: 'Completar cadastro do perfil',
      actionTo: '/onboarding',
    }
  }

  if (status === 422 && code === 'provider_validation') {
    const fieldFeedback = bankProviderFieldFeedback(param)
    if (fieldFeedback) return fieldFeedback

    return {
      kind: 'message',
      message: `Não conseguimos validar os dados da conta bancária. Confira banco, agência e conta.${
        hasActiveAccount
          ? registrationFeedbackEnabled
            ? ' A conta atual permanece cadastrada.'
            : ' A conta atual continua recebendo repasses.'
          : ''
      }`,
    }
  }

  if (status === 502 && code === 'default_switch_failed') {
    return {
      kind: 'switch_failed',
      message:
        'A nova conta foi cadastrada, mas a troca não foi concluída. A conta atual continua ativa. Tente novamente pela lista.',
    }
  }

  if (status === 404 || code === 'bank_account_not_found') {
    return { kind: 'retry', message: 'Conta não encontrada. Atualize a página.' }
  }

  if (status === 503 || status === 502) {
    return {
      kind: 'retry',
      message: 'Não foi possível concluir a ação agora. Tente novamente.',
    }
  }

  return { kind: 'retry', message: 'Não foi possível concluir a ação. Tente novamente.' }
}

function bankProviderFieldFeedback(
  param: string | undefined,
): Extract<BankAccountErrorFeedback, { kind: 'fields' }> | null {
  if (!param) return null
  const normalized = param.replaceAll('[', '.').replaceAll(']', '.')
  if (normalized.endsWith('account_number.')) {
    return { kind: 'fields', fields: { accountNumber: 'Confira o número da conta.' } }
  }
  if (normalized.endsWith('routing_number.')) {
    return {
      kind: 'fields',
      fields: {
        bankCode: 'Confira o banco e a agência.',
        branch: 'Confira o banco e a agência.',
      },
    }
  }
  return null
}
